import type { AxiosResponse } from 'axios';
import { createApiError } from '@/core/error-handling/ApiErrorHandler';

/**
 * Every endpoint in this API wraps its payload in `{ success, data, message }`
 * -- see mdfiles/loginapi.md sections 1-4 and
 * mdfiles/organization-setup-step1-apiflow.md.
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  code?: string;
  traceId?: string;
  timestamp?: string;
  /**
   * Present only when the request caused e-mail (Organization Setup completion, an administrator onboarded
   * to a live organization, an administrator-set password). See {@link EmailDispatchRecord}.
   */
  email?: EmailDispatchRecord;
}

/**
 * What the platform knew about a request's e-mails when it answered.
 *
 * Mail is queued into the notification log and delivered afterwards on the server's own executor, so the
 * most a response can truthfully say is **queued** -- there is deliberately no "sent". `FAILED` and
 * `PARTIAL` mean some mail owed could not even be queued (an unusable address, an incomplete profile).
 * `SUPPRESSED` means nothing new was owed: the recipient had already been sent it.
 */
export interface EmailDispatchRecord {
  status: 'QUEUED' | 'PARTIAL' | 'FAILED' | 'SUPPRESSED';
  queued: number;
  failed: number;
  suppressed: number;
}

/** A payload with the envelope's e-mail report carried alongside it, for the caller that has to show it. */
export type WithEmailDispatch<T> = T & { email?: EmailDispatchRecord | null };

const EMAIL_STATUSES: readonly string[] = ['QUEUED', 'PARTIAL', 'FAILED', 'SUPPRESSED'];

/**
 * The envelope's `email`, or `null` when there is none or it is not the shape this client understands.
 *
 * Read defensively: it is an optional, additive field, and a malformed one must cost the toast, never the
 * payload it rides on.
 */
export function readEmailDispatch(
  response: AxiosResponse<ApiEnvelope<unknown>>,
): EmailDispatchRecord | null {
  const email: unknown = (response.data as { email?: unknown } | undefined)?.email;
  if (!email || typeof email !== 'object') return null;
  const record = email as Partial<Record<keyof EmailDispatchRecord, unknown>>;
  if (typeof record.status !== 'string' || !EMAIL_STATUSES.includes(record.status)) return null;
  const count = (value: unknown): number => (typeof value === 'number' ? value : 0);
  return {
    status: record.status as EmailDispatchRecord['status'],
    queued: count(record.queued),
    failed: count(record.failed),
    suppressed: count(record.suppressed),
  };
}

/**
 * Unwraps the envelope, turning a described failure into a typed ApiError.
 *
 * A 2xx carrying `success: false` is still a failure -- axios will not reject
 * it, so it has to be caught here or it reaches the UI as valid data.
 */
export function unwrapEnvelope<T>(
  response: AxiosResponse<ApiEnvelope<T>>,
  fallbackMessage: string,
): T {
  const body = response.data;

  if (!body || typeof body !== 'object') {
    throw createApiError({
      message: fallbackMessage,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  // A described failure: the envelope's own message is the most specific
  // thing we can show, so it wins.
  if (body.success === false) {
    throw createApiError({
      message: body.message || fallbackMessage,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  // success: true with no payload is malformed, not a described failure --
  // echoing its message ("Request completed successfully") would mislead.
  if (body.data === undefined || body.data === null) {
    throw createApiError({
      message: fallbackMessage,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  return body.data;
}

/**
 * The same check for an endpoint that returns **no payload**.
 *
 * {@link unwrapEnvelope} treats `data: null` as malformed, and for an endpoint that promises a payload it
 * is. A void action is different: `PUT /users/{id}/password` and
 * `PUT /users/{id}/security/password` answer `{ success: true, message: "..." }` with no `data` at all, by
 * design, and putting those through `unwrapEnvelope` turns every success into "Server error, please try
 * again" — which is exactly what the Security tab did.
 *
 * A described failure still throws, so a 2xx carrying `success: false` cannot slip past as it would if the
 * caller simply ignored the response.
 */
export function assertEnvelopeOk(
  response: AxiosResponse<ApiEnvelope<unknown>>,
  fallbackMessage: string,
): void {
  const body = response.data;

  if (!body || typeof body !== 'object' || body.success === false) {
    throw createApiError({
      message: (body && typeof body === 'object' && body.message) || fallbackMessage,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }
}
